import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import portalAPI from '../../services/portalAPI';
import { reportsAPI, triggerDownload } from '../../services/reportsAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import './BatchReportView.css';

interface BatchReportData {
  batch: {
    id: string;
    batch_number: string;
    custom_name: string | null;
    animal_type: string;
    animal_type_display: string;
    quantity_started: number;
    current_count: number;
    arrival_date: string;
    target_harvest_date: string | null;
    status: string;
    status_display: string;
    days_active: number;
  };
  financials: {
    total_costs: number;
    total_revenue: number;
    profit: number;
    cost_per_head: number;
    revenue_per_head: number;
    costs_breakdown: {
      feed: number;
      animals: number;
      supplies: number;
      veterinary: number;
      labor: number;
      other: number;
    };
  };
  mortality: {
    total_deaths: number;
    mortality_rate: number;
    causes: { cause: string; count: number }[];
  };
  production: {
    weights: { date: string; avg_weight: number }[];
    final_avg_weight: number | null;
    eggs_total: number | null;
    milk_total: number | null;
  };
  log_summary: {
    total_entries: number;
    by_type: { type: string; count: number }[];
  };
}

const BatchReportView: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const [data, setData] = useState<BatchReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadReportData();
  }, [batchId]);

  const loadReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch batch details and report summary
      const [batchRes, summaryRes] = await Promise.all([
        portalAPI.get(`/planner/portal/batches/${batchId}/`),
        portalAPI.get(`/planner/portal/batches/${batchId}/report-summary/`).catch(() => ({ data: null }))
      ]);

      const batch = batchRes.data;
      const summary = summaryRes.data;

      // Construct report data - use summary if available, otherwise show basic info
      setData({
        batch: {
          id: batch.id,
          batch_number: batch.batch_number,
          custom_name: batch.custom_name,
          animal_type: batch.animal_type,
          animal_type_display: batch.animal_type_display || batch.animal_type,
          quantity_started: batch.quantity_started,
          current_count: batch.current_count,
          arrival_date: batch.arrival_date,
          target_harvest_date: batch.target_harvest_date,
          status: batch.status,
          status_display: batch.status_display || batch.status,
          days_active: batch.days_active || calculateDaysActive(batch.arrival_date),
        },
        financials: summary?.financials || {
          total_costs: 0,
          total_revenue: 0,
          profit: 0,
          cost_per_head: 0,
          revenue_per_head: 0,
          costs_breakdown: { feed: 0, animals: 0, supplies: 0, veterinary: 0, labor: 0, other: 0 }
        },
        mortality: summary?.mortality || {
          total_deaths: batch.quantity_started - batch.current_count,
          mortality_rate: ((batch.quantity_started - batch.current_count) / batch.quantity_started * 100) || 0,
          causes: []
        },
        production: summary?.production || {
          weights: [],
          final_avg_weight: null,
          eggs_total: null,
          milk_total: null
        },
        log_summary: summary?.log_summary || {
          total_entries: 0,
          by_type: []
        }
      });
    } catch (err: any) {
      console.error('Failed to load report data:', err);
      setError('Failed to load batch report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysActive = (arrivalDate: string) => {
    const arrival = new Date(arrivalDate);
    const now = new Date();
    return Math.floor((now.getTime() - arrival.getTime()) / (1000 * 60 * 60 * 24));
  };

  const handleDownloadPDF = async () => {
    if (!batchId) return;
    setDownloading(true);
    try {
      const blob = await reportsAPI.downloadBatchReport(batchId);
      const filename = `batch_report_${data?.batch.batch_number || batchId}_${new Date().toISOString().split('T')[0]}.pdf`;
      triggerDownload(blob, filename);
    } catch (err: any) {
      console.error('Failed to download report:', err);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="batch-report-view">
        <div className="loading-state">Loading batch report...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="batch-report-view">
        <div className="error-state">
          {error || 'Failed to load report'}
          <button onClick={loadReportData} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  const { batch, financials, mortality, production, log_summary } = data;

  return (
    <div className="batch-report-view">
      <div className="report-header">
        <Link to="/portal/reports/batches" className="back-link">← Back to Batch Reports</Link>

        <div className="header-content">
          <div className="header-left">
            <h1>{batch.custom_name || batch.batch_number}</h1>
            <p className="batch-meta">
              {batch.animal_type_display} • {batch.quantity_started} started • {batch.days_active} days
            </p>
            <span className={`status-badge status-${batch.status}`}>
              {batch.status_display}
            </span>
          </div>

          <div className="header-actions">
            <button
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="download-btn"
            >
              {downloading ? 'Generating PDF...' : 'Download Full Report (PDF)'}
            </button>
            <Link to={`/portal/farmhand/batches/${batch.id}`} className="view-batch-link">
              View Live Batch →
            </Link>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-grid">
        <div className="summary-card">
          <div className="card-label">Current Count</div>
          <div className="card-value">{batch.current_count}</div>
          <div className="card-sub">of {batch.quantity_started} started</div>
        </div>

        <div className="summary-card">
          <div className="card-label">Mortality Rate</div>
          <div className="card-value">{mortality.mortality_rate.toFixed(1)}%</div>
          <div className="card-sub">{mortality.total_deaths} losses</div>
        </div>

        <div className="summary-card profit">
          <div className="card-label">Net Profit/Loss</div>
          <div className={`card-value ${financials.profit >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(financials.profit)}
          </div>
          <div className="card-sub">{formatCurrency(financials.profit / batch.quantity_started)} per head</div>
        </div>

        <div className="summary-card">
          <div className="card-label">Log Entries</div>
          <div className="card-value">{log_summary.total_entries}</div>
          <div className="card-sub">recorded events</div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="report-section">
        <h2>Financial Summary</h2>
        <div className="financial-grid">
          <div className="financial-column">
            <h3>Costs</h3>
            <div className="financial-row total">
              <span>Total Costs</span>
              <span>{formatCurrency(financials.total_costs)}</span>
            </div>
            <div className="costs-breakdown">
              {financials.costs_breakdown.feed > 0 && (
                <div className="financial-row">
                  <span>Feed</span>
                  <span>{formatCurrency(financials.costs_breakdown.feed)}</span>
                </div>
              )}
              {financials.costs_breakdown.animals > 0 && (
                <div className="financial-row">
                  <span>Animals</span>
                  <span>{formatCurrency(financials.costs_breakdown.animals)}</span>
                </div>
              )}
              {financials.costs_breakdown.supplies > 0 && (
                <div className="financial-row">
                  <span>Supplies</span>
                  <span>{formatCurrency(financials.costs_breakdown.supplies)}</span>
                </div>
              )}
              {financials.costs_breakdown.veterinary > 0 && (
                <div className="financial-row">
                  <span>Veterinary</span>
                  <span>{formatCurrency(financials.costs_breakdown.veterinary)}</span>
                </div>
              )}
              {financials.costs_breakdown.labor > 0 && (
                <div className="financial-row">
                  <span>Labor</span>
                  <span>{formatCurrency(financials.costs_breakdown.labor)}</span>
                </div>
              )}
              {financials.costs_breakdown.other > 0 && (
                <div className="financial-row">
                  <span>Other</span>
                  <span>{formatCurrency(financials.costs_breakdown.other)}</span>
                </div>
              )}
            </div>
            <div className="financial-row per-head">
              <span>Cost per Head</span>
              <span>{formatCurrency(financials.cost_per_head)}</span>
            </div>
          </div>

          <div className="financial-column">
            <h3>Revenue</h3>
            <div className="financial-row total">
              <span>Total Revenue</span>
              <span>{formatCurrency(financials.total_revenue)}</span>
            </div>
            <div className="financial-row per-head">
              <span>Revenue per Head</span>
              <span>{formatCurrency(financials.revenue_per_head)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Batch Timeline */}
      <div className="report-section">
        <h2>Batch Timeline</h2>
        <div className="timeline">
          <div className="timeline-item">
            <div className="timeline-dot completed"></div>
            <div className="timeline-content">
              <strong>Arrival</strong>
              <span>{formatDate(batch.arrival_date)}</span>
            </div>
          </div>
          {batch.target_harvest_date && (
            <div className="timeline-item">
              <div className={`timeline-dot ${batch.status === 'harvested' ? 'completed' : 'pending'}`}></div>
              <div className="timeline-content">
                <strong>{batch.status === 'harvested' ? 'Harvested' : 'Target Harvest'}</strong>
                <span>{formatDate(batch.target_harvest_date)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Log Summary */}
      {log_summary.by_type.length > 0 && (
        <div className="report-section">
          <h2>Activity Log Summary</h2>
          <div className="log-summary-grid">
            {log_summary.by_type.map(item => (
              <div key={item.type} className="log-type-card">
                <div className="log-type-count">{item.count}</div>
                <div className="log-type-name">{item.type}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div className="report-cta">
        <p>For the complete report with detailed logs, weight charts, and weather data:</p>
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="download-btn large"
        >
          {downloading ? 'Generating...' : 'Download Full PDF Report'}
        </button>
      </div>

      <PortalFooter />
    </div>
  );
};

export default BatchReportView;
